import { useQuery } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import { POLL_CURATION_MS } from "@/lib/realtime";
import { useIsBrandLive, useLiveRefetchInterval } from "@/lib/socket";

export const pendingSignalCountKey = (brandId: string | null) => [
  "marketSignalsPendingCount",
  brandId,
];

/**
 * Number of pending market signals awaiting review for a brand. Backs the sidebar
 * notification badge, so a freshly filed signal nudges the user without a manual refresh:
 * realtime hints refresh it while the brand is live; otherwise it polls every minute and
 * refetches on window focus. Returns 0 until loaded.
 */
export function usePendingSignalCount(brandId: string | null): number {
  const live = useIsBrandLive(brandId);
  const refetchInterval = useLiveRefetchInterval(brandId, POLL_CURATION_MS);
  const query = useQuery({
    queryKey: pendingSignalCountKey(brandId),
    queryFn: async () => {
      const res = await axiosInstance.get<{ count: number }>(
        `/market-signals/${brandId}/pending-count`,
      );
      return res.data.count;
    },
    enabled: !!brandId,
    refetchInterval,
    refetchOnWindowFocus: !live,
  });
  return query.data ?? 0;
}
